import { autoUpdate, computePosition, detectOverflow, shift, } from "@floating-ui/dom";
import Loading from "./Loading.svelte";
import { AppStore, AppType, Panels } from "$/stores/AppStore";
import { get } from "svelte/store";
import { getRootDocument, isExtensionOrPdfViewer, SNIPPETS_WRAPPER_ID, SUGGESTION_LIST_ID, PAGE_WRAPPER_ID, } from "$/utils/Utils";
import { isMobile } from "$/utils/isMobile";
const EXTENSION_Z_INDEX = 50;
const HIGHLIGHTER_Z_INDEX = 2147483647;
const WEB_Z_INDEX = 40;
let cleanupAfterRemoval;
const floatingUIMiddleware = (target) => ({
    name: "middleware",
    async fn(state) {
        await detectOverflow(state, {
            boundary: target,
        });
        return {};
    },
});
function isHighlighterOrSnippetPreview() {
    return (get(AppStore).type === AppType.HIGHLIGHTER ||
        (window.location.href.split("/").pop() === "intro" &&
            get(AppStore).type === AppType.DASHBOARD) ||
        get(AppStore).panel === Panels.SNIPPET);
}
export async function positionSuggestionList(referenceElement, virtualElement, floatingElement) {
    return new Promise((resolve, reject) => (cleanupAfterRemoval = autoUpdate(referenceElement, floatingElement, () => {
        let suggestionListPlacement = "bottom-start";
        if (referenceElement.dataset.reversesuggestionlist ===
            "true") {
            suggestionListPlacement = "top-start";
        }
        computePosition(virtualElement, floatingElement, {
            placement: suggestionListPlacement,
            middleware: [
                shift({ padding: 5 }),
                floatingUIMiddleware(referenceElement),
            ],
        })
            .then(({ y }) => {
            let positonLeft;
            if (isExtensionOrPdfViewer()) {
                const computedStyle = window.getComputedStyle(getRootDocument("extension").getElementById(SNIPPETS_WRAPPER_ID));
                const leftPadding = computedStyle.getPropertyValue("padding-left");
                positonLeft = parseInt(leftPadding) + 3;
            }
            else {
                positonLeft =
                    (referenceElement === null || referenceElement === void 0 ? void 0 : referenceElement.getBoundingClientRect().left) + 3;
            }
            // positioning list to be a little bit smaller than the card and
            // to be in the middle of the card
            Object.assign(floatingElement.style, {
                top: `${y}px`,
                left: `${positonLeft}px`,
                width: `${(referenceElement === null || referenceElement === void 0 ? void 0 : referenceElement.offsetWidth) - 6}px`,
            });
            requestAnimationFrame(() => {
                Object.assign(floatingElement.style, {
                    opacity: 1,
                });
                resolve();
            });
        })
            .catch((error) => {
            reject(error);
        });
    })));
}
async function showSuggestionList(parentWrapper, editorProps, rootTarget) {
    var _a;
    let wrapper;
    let component;
    let zIndex;
    (_a = getRootDocument().getElementById("suggestionListLoader")) === null || _a === void 0 ? void 0 : _a.remove();
    let virtualElement = {
        getBoundingClientRect() {
            var _a;
            return (_a = editorProps.clientRect) === null || _a === void 0 ? void 0 : _a.call(editorProps);
        },
    };
    wrapper = document.createElement("div");
    wrapper.setAttribute("id", "suggestionListLoader");
    rootTarget.appendChild(wrapper);
    component = new Loading({
        target: wrapper,
    });
    if (isExtensionOrPdfViewer()) {
        zIndex = EXTENSION_Z_INDEX;
    }
    else if (isHighlighterOrSnippetPreview()) {
        zIndex = HIGHLIGHTER_Z_INDEX;
    }
    else {
        zIndex = WEB_Z_INDEX;
    }
    Object.assign(wrapper.style, {
        position: "absolute",
        zIndex: zIndex,
        opacity: 0,
        transition: "opacity 0.2s ease-in-out",
    });
    if (wrapper && parentWrapper && virtualElement) {
        try {
            await positionSuggestionList(parentWrapper, virtualElement, wrapper);
        }
        catch (e) {
            wrapper.remove();
        }
    }
}
export function removeSuggestionList() {
    var _a;
    (_a = getRootDocument().getElementById(SUGGESTION_LIST_ID)) === null || _a === void 0 ? void 0 : _a.remove();
}
export function sortWorkspaceMembers(members) {
    return members.sort((a, b) => {
        const firstCompare = (a.firstName + a.lastName ? a.firstName + a.lastName : a.username).toLowerCase();
        const secondCompare = (b.firstName + b.lastName ? b.firstName + b.lastName : b.username).toLowerCase();
        if (firstCompare < secondCompare) {
            return 1;
        }
        if (firstCompare > secondCompare) {
            return -1;
        }
        return 0;
    });
}
export function editorSuggestion(search, listComponent, userRole, parentWrapper, mentions = []) {
    let rootDocument = null;
    let rootTarget = null;
    let zIndex = 0;
    if (isExtensionOrPdfViewer()) {
        rootDocument = getRootDocument("extension");
        rootTarget = rootDocument.getElementById(SNIPPETS_WRAPPER_ID);
        zIndex = EXTENSION_Z_INDEX;
    }
    else if (get(AppStore).type === AppType.HIGHLIGHTER) {
        rootDocument = rootTarget = getRootDocument();
        zIndex = HIGHLIGHTER_Z_INDEX;
    }
    else {
        rootDocument = getRootDocument("web");
        rootTarget = rootDocument.getElementById(PAGE_WRAPPER_ID);
        if (isHighlighterOrSnippetPreview()) {
            zIndex = HIGHLIGHTER_Z_INDEX;
        }
        else {
            zIndex = WEB_Z_INDEX;
        }
    }
    return {
        items: async ({ query }) => {
            return await search(query);
        },
        render: () => {
            let wrapper;
            let component;
            return {
                async onBeforeStart(props) {
                    await showSuggestionList(parentWrapper, props, rootTarget);
                },
                async onStart(props) {
                    var _a, _b;
                    (_a = rootDocument.getElementById(SUGGESTION_LIST_ID)) === null || _a === void 0 ? void 0 : _a.remove();
                    (_b = rootDocument
                        .getElementById("suggestionListLoader")) === null || _b === void 0 ? void 0 : _b.remove();
                    let virtualElement = {
                        getBoundingClientRect() {
                            var _a;
                            return (_a = props.clientRect) === null || _a === void 0 ? void 0 : _a.call(props);
                        },
                    };
                    wrapper = document.createElement("div");
                    wrapper.setAttribute("id", SUGGESTION_LIST_ID);
                    rootTarget.appendChild(wrapper);
                    let inverseList = parentWrapper.dataset.reversesuggestionlist === "true";
                    component = new listComponent({
                        target: wrapper,
                        props: {
                            props,
                            userRole: userRole,
                            mentions,
                            inverseList,
                        },
                    });
                    Object.assign(wrapper.style, {
                        position: "absolute",
                        zIndex: zIndex,
                        opacity: 0,
                        transition: "opacity 0.2s ease-in-out",
                    });
                    if (wrapper && parentWrapper && virtualElement) {
                        try {
                            await positionSuggestionList(parentWrapper, virtualElement, wrapper);
                        }
                        catch (e) {
                            wrapper === null || wrapper === void 0 ? void 0 : wrapper.remove();
                        }
                    }
                },
                onUpdate: async (props) => {
                    component === null || component === void 0 ? void 0 : component.$set({ props });
                    wrapper = rootDocument.getElementById(SUGGESTION_LIST_ID);
                    const virtualElement = {
                        getBoundingClientRect() {
                            var _a;
                            return (_a = props.clientRect) === null || _a === void 0 ? void 0 : _a.call(props);
                        },
                    };
                    //recalculatiing suggestion list position in cases when the user starts typing
                    //close to the end of the line and eventually ends in new line
                    if (wrapper && parentWrapper && virtualElement) {
                        try {
                            await positionSuggestionList(parentWrapper, virtualElement, wrapper);
                        }
                        catch (e) {
                            wrapper === null || wrapper === void 0 ? void 0 : wrapper.remove();
                        }
                    }
                },
                onKeyDown: (props) => {
                    if (component &&
                        typeof component.onKeyDown === "function") {
                        return component.onKeyDown(props);
                    }
                },
                onExit: () => {
                    var _a;
                    (_a = rootDocument
                        .getElementById("suggestionListLoader")) === null || _a === void 0 ? void 0 : _a.remove();
                    component === null || component === void 0 ? void 0 : component.$destroy();
                    wrapper === null || wrapper === void 0 ? void 0 : wrapper.remove();
                    cleanupAfterRemoval === null || cleanupAfterRemoval === void 0 ? void 0 : cleanupAfterRemoval();
                },
            };
        },
    };
}
